import { ensureDefined } from "common/utils/assertOfType";

/**
 * Types and functions for using binary space partitioning trees to clip various types of geometry including
 * faces and PolyLineX paths.
 * @module
 */
import { aabb2AddPoint } from "./aabb2";
import { aabb2New } from "./aabb2Types";
import type { Face } from "./brep";
import { faceIsNotNull, faceOrNullMove, faceNew, facesAABB3, faceCopy, tagFaces } from "./brep";
import {
  addConvexPlanesToBSPLeaf,
  buildBSPNode,
  buildEdgePolygon,
  buildPolygonsFromFaces,
  classifyPolygonBSP,
  isDegeneratePolygonBSP,
  polyLine3FromLineBSP,
  renderNodeBSP,
  splitLinesBSP,
  splitPolygonBSP,
  splitPolygonsBSP,
} from "./bsp";
import type { LineBSP, MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP, RenderInfoBSP } from "./bspTypes";
import { BSP_EPSILON, TraversalCullingBSP, TraversalOrderBSP } from "./bspTypes";
import { DIST_EPSILON_SQUARED } from "./core";
import { lineBSPNew } from "./lineBSPNew";
import {
  PlaneSide,
  planeByThreePoints,
  planeCopy,
  planeMove,
  planeNewXY,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeReverse,
  polyLine2OnPlane,
} from "./plane";
import { planeBSPFromPlane } from "./planeBSP";
import type { Plane } from "./planeTypes";
import { polyLine2SetCW, polyLine2FromAABB2 } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { polyLine2New } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3Concat, polyLine3Copy, polyLine3EndPoint, polyLine3Simplify, polyLine3StartPoint } from "./polyLine3";
import { pushArray } from "./utils";
import { vec2DistSquared } from "./vec2";
import type { Vec3 } from "./vec3";
import { vec3DistSquared, vec3Dot, vec3Sub } from "./vec3";

export interface ClippingNode {
  root: NodeBSP | null;
  revert: boolean;
}

export function clippingNodeNew(root: NodeBSP | null, revert: boolean = false): ClippingNode {
  return {
    root,
    revert,
  };
}

export interface ClipFaceContext {
  clippingNodes: ClippingNode[];
  coplanarsToFront?: boolean;
  checkCoplanarFacing?: boolean;
  epsilon?: number;
}

export interface ClipFaceOptions {
  face: Face;
  options: ClipFaceContext;
  clippedFaces?: Face[];
}

export function clipFace({ face, options, clippedFaces = [] }: ClipFaceOptions): Face[] {
  const clipped = clipFaces({
    faces: [face],
    options,
    clippedFaces,
  });
  if (face.tags) {
    return tagFaces(clipped, face.tags);
  }
  return clipped;
}

export interface ClipFacesOptions {
  faces: Face[];
  options: ClipFaceContext;
  clippedFaces?: Face[];
  offsetZ?: number;
  normalOffset?: number;
}

export function clipFaces({ faces, options, clippedFaces, offsetZ, normalOffset }: ClipFacesOptions): Face[] {
  return clipFacesWithMultipleContexts({
    faces,
    optionList: [options],
    clippedFaces,
    offsetZ,
    normalOffset,
  });
}

export interface ClipFacesWithMultipleContextsOptions {
  faces: Face[];
  optionList: ClipFaceContext[];
  clippedFaces?: Face[];
  offsetZ?: number;
  normalOffset?: number;
}

export function clipFacesWithMultipleContexts({
  faces,
  optionList,
  clippedFaces = [],
  offsetZ = 0,
  normalOffset = 0,
}: ClipFacesWithMultipleContextsOptions): Face[] {
  const filteredOptions: ClipFaceContext[] = [];
  for (const options of optionList) {
    const filteredClippingNodes = options.clippingNodes.filter((x) => x?.root);

    if (filteredClippingNodes.length === 0) {
      continue;
    }

    filteredOptions.push({
      ...options,
      clippingNodes: filteredClippingNodes,
    });
  }

  if (filteredOptions.length === 0) {
    pushArray(clippedFaces, faces.filter(faceIsNotNull).map(faceCopy));
    return clippedFaces;
  }

  // build polygon from the face
  let inputPolygons: PolygonBSP[] = [];

  for (let i = 0, l = faces.length; i < l; i++) {
    pushArray(inputPolygons, buildPolygonsFromFaces({ faces: [faces[i]], priority: i, offsetZ, normalOffset }));
  }

  let outputPolygons: PolygonBSP[] = [];

  for (const options of filteredOptions) {
    const { clippingNodes, coplanarsToFront = false, checkCoplanarFacing = false, epsilon = BSP_EPSILON } = options;

    for (const clippingNode of clippingNodes) {
      outputPolygons = [];
      splitPolygonsBSP({
        node: clippingNode.root,
        polygons: inputPolygons,
        coplanarsToFront,
        frontPolygons: clippingNode.revert ? undefined : outputPolygons,
        backPolygons: clippingNode.revert ? outputPolygons : undefined,
        epsilon,
        checkCoplanarFacing,
      });

      const tmpPolygons = inputPolygons;
      inputPolygons = outputPolygons;
      outputPolygons = tmpPolygons;
    }
  }

  // rebuild faces
  for (let i = 0, l = inputPolygons.length; i < l; i++) {
    const polygon = inputPolygons[i];
    if (!isDegeneratePolygonBSP(polygon)) {
      const src = faces[polygon.priority];
      const f = buildFaceFromPolygonBSP(polygon, src.plane, -offsetZ);
      if (f) {
        if (src.tags) {
          f.tags = [...src.tags];
        }
        clippedFaces.push(f);
      }
    }
  }

  return clippedFaces;
}

interface ClipFacesWithPlaneOptions {
  faces: Face[];
  plane: Plane;
  coplanarsToFront?: boolean;
  epsilon?: number;
  addCapFaces?: boolean;
  maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

export function clipFacesWithPlane({
  faces,
  plane,
  addCapFaces,
  coplanarsToFront,
  epsilon,
  maxDepthErrorHandle,
}: ClipFacesWithPlaneOptions): Face[] {
  if (faces.length === 0) {
    return faces;
  }

  let volumeBSP: NodeBSP | null = null;
  let aabbPoints: Vec3[] = [];
  if (addCapFaces) {
    volumeBSP = buildBSPNode({
      polygons: buildPolygonsFromFaces({ faces }),
      epsilon,
      maxDepthErrorHandle,
    });

    const { minX, minY, minZ, maxX, maxY, maxZ } = facesAABB3(faces);
    aabbPoints = [
      [minX, minY, minZ],
      [maxX, minY, minZ],
      [maxX, maxY, minZ],
      [minX, maxY, minZ],
      [minX, minY, maxZ],
      [maxX, minY, maxZ],
      [maxX, maxY, maxZ],
      [minX, maxY, maxZ],
    ];
  }

  const clippingBSP = ensureDefined(addConvexPlanesToBSPLeaf([planeBSPFromPlane(plane)]));

  faces = clipFaces({
    faces,
    options: {
      clippingNodes: [clippingNodeNew(clippingBSP, true)],
      coplanarsToFront,
      epsilon,
    },
  });

  if (addCapFaces) {
    let aabb2;
    for (const point of aabbPoints) {
      const [u, v] = planeProject(plane, point[0], point[1], point[2]);
      aabb2 = aabb2 ? aabb2AddPoint(aabb2, u, v) : aabb2New(u, v, u, v);
    }

    const bigCapFaceLoop = polyLine2SetCW(polyLine2FromAABB2(aabb2));
    const bigCapFace = faceNew([bigCapFaceLoop], plane);

    if (volumeBSP) {
      clipFace({
        face: bigCapFace,
        options: {
          clippingNodes: [clippingNodeNew(volumeBSP, true)],
          coplanarsToFront,
          epsilon,
        },
        clippedFaces: faces,
      });
    }
  }

  return faces;
}

interface ClipFacesWithPlanesOptions {
  faces: Face[];
  planes: Plane[];
  coplanarsToFront?: boolean;
  epsilon?: number;
  addCapFaces?: boolean;
  maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

export function clipFacesWithPlanes({
  faces,
  planes,
  addCapFaces,
  coplanarsToFront,
  epsilon,
  maxDepthErrorHandle,
}: ClipFacesWithPlanesOptions): Face[] {
  for (const plane of planes) {
    faces = clipFacesWithPlane({
      faces,
      plane,
      addCapFaces,
      coplanarsToFront,
      epsilon,
      maxDepthErrorHandle,
    });
  }

  return faces;
}

export interface ClipRailContext {
  clippingNodes: ClippingNode[];
  coplanarsToFront?: boolean;
  epsilon?: number;
}

interface ClipRails3Options {
  rails: PolyLine3[];
  options: ClipRailContext;
  clippedRails?: PolyLine3[];
}

export function clipRails3({ rails, options, clippedRails = [] }: ClipRails3Options): PolyLine3[] {
  for (let i = 0, l = rails.length; i < l; i++) {
    clipRail3({
      rail: rails[i],
      options,
      clippedRails,
    });
  }

  return clippedRails;
}

interface ClipRail3Options {
  rail: PolyLine3;
  options: ClipRailContext;
  clippedRails?: PolyLine3[];
}

export function clipRail3({ rail, options, clippedRails = [] }: ClipRail3Options): PolyLine3[] {
  const { clippingNodes, coplanarsToFront = false, epsilon = BSP_EPSILON } = options;

  let curRail: PolyLine3 | null = null;

  const addCurRail = () => {
    if (curRail) {
      clippedRails.push(polyLine3Simplify(curRail));
    }
  };

  const filteredClippingNodes = clippingNodes.filter((x) => x?.root);
  if (filteredClippingNodes.length === 0) {
    clippedRails.push(polyLine3Copy(rail));

    return clippedRails;
  }

  const rails: LineBSP[] = [];

  for (let i = 0, l = rail.length - 3; i < l; i += 3) {
    let inputSegments = [lineBSPNew(rail[i], rail[i + 1], rail[i + 2], rail[i + 3], rail[i + 4], rail[i + 5])];

    let outputSegments: LineBSP[] = [];

    for (const { root, revert } of filteredClippingNodes) {
      outputSegments = [];

      splitLinesBSP({
        node: root,
        coplanarsToFront,
        segments: inputSegments,
        frontSegments: revert ? undefined : outputSegments,
        backSegments: revert ? outputSegments : undefined,
        epsilon,
      });

      const tmpSegments = inputSegments;
      inputSegments = outputSegments;
      outputSegments = tmpSegments;
    }

    const subRails = inputSegments;

    if (subRails.length > 0) {
      // sort the sub rails
      const { x1, y1, z1, x2, y2, z2 } = subRails[0];
      const [railDirX, railDirY, railDirZ] = vec3Sub(x2, y2, z2, x1, y1, z1);

      subRails.sort(
        ({ x1: xa, y1: ya, z1: za }, { x1: xb, y1: yb, z1: zb }) =>
          vec3Dot(xa, ya, za, railDirX, railDirY, railDirZ) - vec3Dot(xb, yb, zb, railDirX, railDirY, railDirZ),
      );

      // add them to the global list
      pushArray(rails, subRails);
    }
  }

  if (rails.length > 0) {
    // try to concat the resulting rail
    for (let i = 0, l = rails.length; i < l; i++) {
      if (curRail) {
        if (
          vec3DistSquared(
            curRail[curRail.length - 3],
            curRail[curRail.length - 2],
            curRail[curRail.length - 1],
            rails[i].x1,
            rails[i].y1,
            rails[i].z1,
          ) <= epsilon
        ) {
          curRail = polyLine3Concat(curRail, polyLine3FromLineBSP(rails[i]));
        } else {
          addCurRail();
          curRail = null;
        }
      }

      if (!curRail) {
        curRail = polyLine3FromLineBSP(rails[i]);
      }
    }

    if (curRail) {
      addCurRail();
    }

    const clippedRailCount = clippedRails.length;
    if (clippedRailCount > 1) {
      const firstRail = clippedRails[0];
      const lastRail = clippedRails[clippedRailCount - 1];

      const [startX, startY, startZ] = polyLine3StartPoint(firstRail);
      const [endX, endY, endZ] = polyLine3EndPoint(lastRail);

      if (vec3DistSquared(startX, startY, startZ, endX, endY, endZ) < DIST_EPSILON_SQUARED) {
        clippedRails[0] = polyLine3Simplify(polyLine3Concat(lastRail, firstRail));
        clippedRails.pop();
      }
    }
  }

  return clippedRails;
}

interface ClipRailsOptions {
  rails: PolyLine2[];
  height: number;
  options: ClipRailContext;
  clippedRails?: PolyLine3[];
}

export function clipRails({ rails, options, height, clippedRails = [] }: ClipRailsOptions): PolyLine2[] {
  for (let i = 0, l = rails.length; i < l; i++) {
    clipRail({
      rail: rails[i],
      height,
      options,
      clippedRails,
    });
  }

  return clippedRails;
}

interface ClipRailOptions {
  rail: PolyLine2;
  height: number;
  options: ClipRailContext;
  clippedRails?: PolyLine2[];
}

export function clipRail({ rail, height, options, clippedRails = [] }: ClipRailOptions): PolyLine2[] {
  const plane = planeNewXY(0, 0, height);

  return clipRailOnPlane({
    rail,
    plane,
    options,
    clippedRails,
  });
}

interface ClipRailOnPlaneOptions {
  rail: PolyLine2;
  plane: Plane;
  options: ClipRailContext;
  clippedRails?: PolyLine2[];
}

export function clipRailOnPlane({ rail, plane, options, clippedRails = [] }: ClipRailOnPlaneOptions): PolyLine2[] {
  const rails = clipRail3({
    rail: polyLine2OnPlane(plane, rail),
    options,
  });

  pushArray(
    clippedRails,
    rails.map((x) => planeProjectPolyLine3(plane, x)),
  );

  return clippedRails;
}

export function splitFacesBSP(faces: Face[], options: SplitFaceBSPOptions) {
  const { node, coplanarsToFront = false, frontFaces = null, backFaces = null, epsilon = BSP_EPSILON } = options;

  // build polygon from the face
  const polygons: PolygonBSP[] = [];
  for (let i = 0, l = faces.length; i < l; i++) {
    pushArray(polygons, buildPolygonsFromFaces({ faces: [faces[i]], priority: i }));
  }

  // split polygons;
  const frontPolygons: PolygonBSP[] = [];
  const backPolygons: PolygonBSP[] = [];
  splitPolygonsBSP({
    node,
    polygons,
    coplanarsToFront,
    frontPolygons,
    backPolygons,
    epsilon,
  });

  // rebuild faces
  if (frontFaces) {
    for (let i = 0, l = frontPolygons.length; i < l; i++) {
      const polygon = frontPolygons[i];
      if (!isDegeneratePolygonBSP(polygon)) {
        const f = buildFaceFromPolygonBSP(polygon, faces[polygon.priority].plane);
        if (f) {
          frontFaces.push(f);
        }
      }
    }
  }

  if (backFaces) {
    for (let i = 0, l = backPolygons.length; i < l; i++) {
      const polygon = backPolygons[i];
      if (!isDegeneratePolygonBSP(polygon)) {
        const f = buildFaceFromPolygonBSP(polygon, faces[polygon.priority].plane);
        if (f) {
          backFaces.push(f);
        }
      }
    }
  }
}

export interface SplitFaceBSPOptions {
  node: NodeBSP | null;
  coplanarsToFront?: boolean;
  frontFaces?: Face[];
  backFaces?: Face[];
  epsilon?: number;
}

export function splitFaceBSP(face: Face, options: SplitFaceBSPOptions) {
  splitFacesBSP([face], options);
}

export function buildFaceFromPolygonBSP(
  polygon: PolygonBSP,
  facePlane: Plane | null = null,
  offsetZ: number = 0,
): Face | null {
  const { vertices, plane } = polygon;

  console.assert(vertices.length >= 9, "polygon must have at least 3 vertices");

  let finalFacePlane: Plane | null = facePlane;

  // build the face plane if not provided
  if (finalFacePlane) {
    finalFacePlane = planeCopy(finalFacePlane);
  } else {
    const { nX, nY, nZ } = plane;

    finalFacePlane = planeByThreePoints(
      vertices[0],
      vertices[1],
      vertices[2],
      vertices[3],
      vertices[4],
      vertices[5],
      vertices[6],
      vertices[7],
      vertices[8],
    );

    if (!finalFacePlane) {
      return null;
    }

    const [newNormalX, newNormalY, newNormalZ] = planeNormal(finalFacePlane);

    if (vec3Dot(nX, nY, nZ, newNormalX, newNormalY, newNormalZ) < 0.0) {
      finalFacePlane = planeReverse(finalFacePlane);
    }
  }

  planeMove(finalFacePlane, 0, 0, offsetZ);

  // build the face loop
  const loop = polyLine2New((vertices.length / 3) * 2 + 2);
  for (let j = 0, k = 0, lj = vertices.length; j < lj; j += 3, k += 2) {
    const [u, v] = planeProject(finalFacePlane, vertices[j], vertices[j + 1], vertices[j + 2]);
    loop[k] = u;
    loop[k + 1] = v;
  }

  // close loop
  const loopLength = loop.length;
  loop[loopLength - 2] = loop[0];
  loop[loopLength - 1] = loop[1];

  // force CW
  polyLine2SetCW(loop);

  return faceNew([loop], finalFacePlane);
}

export function addEdgeToPolygonList(
  pl: PolyLine2,
  edgeIndex: number,
  bottomElevation: number,
  topElevation: number,
  rootBSP: NodeBSP | null,
  polygonList: PolygonBSP[],
  epsilon: number,
) {
  const componentIndex = edgeIndex * 2;

  const ax = pl[componentIndex];
  const ay = pl[componentIndex + 1];
  const bx = pl[componentIndex + 2];
  const by = pl[componentIndex + 3];

  if (vec2DistSquared(ax, ay, bx, by) < DIST_EPSILON_SQUARED) {
    return;
  }

  const edgePolygon = buildEdgePolygon(
    ax,
    ay,
    bottomElevation,
    bx,
    by,
    bottomElevation,
    0.0,
    0.0,
    topElevation - bottomElevation,
  );

  if (edgePolygon) {
    splitPolygonBSP({
      node: rootBSP,
      polygon: edgePolygon,
      coplanarsToFront: true,
      frontPolygons: polygonList,
      epsilon,
    });
  }
}

export function addFacesToPolygonList(faces: Face[], rootBSP: NodeBSP, polygonList: PolygonBSP[], epsilon: number) {
  const polygons = buildPolygonsFromFaces({ faces });
  for (let i = 0, l = polygons.length; i < l; i++) {
    splitPolygonBSP({
      node: rootBSP,
      polygon: polygons[i],
      coplanarsToFront: true,
      frontPolygons: polygonList,
      epsilon,
    });
  }
}

export function getFacesFromBSP(root: NodeBSP, offsetX: number = 0, offsetY: number = 0, offsetZ: number = 0): Face[] {
  const renderInfo: RenderInfoBSP = {
    isPerspectiveMatrix: false,
    viewX: 0,
    viewY: 1,
    viewZ: 0,
    viewPosX: 0,
    viewPosY: 0,
    viewPosZ: 0,
  };

  const bspFaces: Face[] = [];
  function renderNodeFunc(node: NodeBSP, _side: number) {
    const { polygons } = node;

    for (let i = 0, l = polygons.length; i < l; i++) {
      const f = buildFaceFromPolygonBSP(polygons[i]);
      if (!f) {
        continue;
      }

      const face = faceOrNullMove(f, offsetX, offsetY, offsetZ);

      if (!face) {
        continue;
      }

      bspFaces.push(face);
    }
  }

  renderNodeBSP({
    node: root,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: TraversalCullingBSP.NONE,
    renderFunc: renderNodeFunc,
  });

  return bspFaces;
}

export function isOverlappingPolygon(polygon: PolygonBSP, node: NodeBSP | null, epsilon: number = BSP_EPSILON) {
  function isOverlappingPolygonImpl(node: NodeBSP | null): boolean {
    if (!node) {
      return false;
    }

    const { plane, frontNode, backNode } = node;

    const side = classifyPolygonBSP(plane, polygon, false, epsilon);

    if (side === PlaneSide.INTERSECTS) {
      return isOverlappingPolygonImpl(backNode) || isOverlappingPolygonImpl(frontNode);
    } else if (side === PlaneSide.BACK) {
      return isOverlappingPolygonImpl(backNode);
    } else if (side === PlaneSide.FRONT) {
      return isOverlappingPolygonImpl(frontNode);
    } else {
      return true;
    }
  }

  const insidePolygons: PolygonBSP[] = [];
  splitPolygonBSP({
    node,
    polygon,
    coplanarsToFront: false,
    backPolygons: insidePolygons,
    epsilon,
  });

  if (insidePolygons.length === 0) {
    return false;
  }

  return isOverlappingPolygonImpl(node);
}

export function isOverlappingFace(face: Face, node: NodeBSP | null, epsilon: number = BSP_EPSILON): boolean {
  // build polygon from the face
  const inputPolygons: PolygonBSP[] = buildPolygonsFromFaces({ faces: [face], priority: 0 });

  for (let i = 0, l = inputPolygons.length; i < l; i++) {
    if (isOverlappingPolygon(inputPolygons[i], node, epsilon)) {
      return true;
    }
  }

  return false;
}
